import React, { useCallback, useEffect, useState } from "react";
import { Switch, notification } from "antd";
import { CalendarOutlined, InfoCircleOutlined } from "@ant-design/icons";
import DataProvider from "../../apis";
import LoadWrapper from "../elements/load-wrapper";
import { useAppStore } from "../../store";
import Colors from "../../constants/Colors";

export default function CalendarSync(props: { tab?: string }) {
  const [api, contextHolder] = notification.useNotification();
  const { setUser } = useAppStore();
  const { tab } = props;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);

  const getProfile = useCallback(async () => {
    setLoading(true);
    const { data } = await DataProvider.getProfile();
    setUser({ ...data });
    // Backend column defaults true; `?? true` covers a cached /auth/me
    // response from before this field existed.
    setEnabled(data.doctor?.calendarInvitesEnabled ?? true);
    setLoading(false);
  }, [setUser]);

  useEffect(() => {
    if (tab === "6") {
      getProfile();
    }
  }, [tab, getProfile]);

  const onToggle = async (value: boolean) => {
    setEnabled(value);
    setSaving(true);
    try {
      await DataProvider.updateMyDoctorProfile({
        calendarInvitesEnabled: value,
      });
    } catch (error: unknown) {
      // Roll back — the switch should reflect what's actually saved, not
      // what was tapped, once the request has failed.
      setEnabled(!value);
      const msg =
        error && typeof error === "object" && "message" in error
          ? String((error as { message?: string }).message)
          : "Could not update your calendar sync setting.";
      api.error({ message: "Could not save", description: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="min-h-[70vh] w-full rounded-2xl p-4 md:p-6"
      style={{
        background:
          "linear-gradient(160deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)",
      }}
    >
      <div className="mx-auto max-w-3xl">
        <section
          className="flex flex-col rounded-2xl border border-slate-200/80 bg-white/95 p-6 shadow-lg shadow-slate-200/50 backdrop-blur-sm md:p-8"
          aria-labelledby="calendar-sync-heading"
        >
          <div className="mb-6 flex items-center gap-3">
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-white shadow-md"
              style={{
                background: "linear-gradient(135deg, #00A2F8 0%, #0070C0 100%)",
              }}
              aria-hidden
            >
              <CalendarOutlined className="text-xl" />
            </div>
            <div>
              <h2
                id="calendar-sync-heading"
                className="text-xl font-semibold tracking-tight text-slate-800"
              >
                Calendar sync
              </h2>
              <p className="text-sm text-slate-500">
                Keep your personal calendar in step with your appointments.
              </p>
            </div>
          </div>

          <LoadWrapper loading={loading}>
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-4">
                <p className="text-sm font-medium text-slate-700">
                  Add appointments to my calendar
                </p>
                <Switch
                  checked={enabled}
                  onChange={onToggle}
                  disabled={saving}
                  aria-label="Add appointments to my calendar"
                  // Explicit background for BOTH states, not just checked —
                  // Tailwind's `@tailwind base` preflight in this app has
                  // already been found to strip antd controls' own
                  // background (see add-medication-modal.tsx's okButtonProps
                  // fix); an unstyled "off" track could end up just as
                  // invisible as that button was.
                  style={{
                    backgroundColor: enabled
                      ? Colors.primary
                      : "rgba(0, 0, 0, 0.25)",
                  }}
                />
              </div>

              {/* Mini info card — explains the mechanism and the payoff of
                  keeping this on, kept separate from the toggle row itself
                  so that row stays a clean, scannable label + switch. */}
              <div className="flex items-start gap-3 rounded-xl border border-sky-100 bg-sky-50/70 px-4 py-4">
                <InfoCircleOutlined
                  className="mt-0.5 shrink-0 text-base"
                  style={{ color: Colors.primary }}
                  aria-hidden
                />
                <div className="space-y-2 text-xs text-slate-600">
                  <p>
                    Confirmed, rescheduled, and cancelled appointments are
                    sent to your email as a calendar invite, so your phone
                    and calendar app (Google, Outlook, Apple Mail, Yahoo,
                    Zoho…) remind you too — on top of MedPharma's own email
                    and push notifications.
                  </p>
                  <p>
                    Keeping this on means you're less likely to miss an
                    appointment — it'll already be sitting on your calendar
                    with its own reminder, even if an email or push
                    notification slips by.
                  </p>
                </div>
              </div>
            </div>
          </LoadWrapper>
        </section>
      </div>
      {contextHolder}
    </div>
  );
}
